import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { Loader2, Check, X, Calendar, Clock, MessageSquare, Inbox } from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
  declined: 'bg-red-500/20 text-red-400 border-red-500/30',
  completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  cancelled: 'bg-muted/40 text-muted-foreground border-border/30',
};

export default function BookingInbox() {
  const { getAuthHeaders } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => { fetchBookings(); }, []);

  const fetchBookings = async () => {
    try {
      const res = await fetch(`${API}/bookings/me`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setBookings(d.bookings || []); }
    } catch (e) { console.error('Fetch bookings error:', e); }
    setLoading(false);
  };

  const updateStatus = async (bookingId, status) => {
    setUpdatingId(bookingId);
    try {
      const res = await fetch(`${API}/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (err) { toast.error(err.message); }
    setUpdatingId(null);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="booking-inbox">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2 mb-4"><Inbox className="w-5 h-5" />Booking Requests</h3>

      {bookings.length === 0 ? (
        <Card className="glass-card border-border p-8 text-center">
          <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No booking requests yet.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {bookings.map(b => (
            <Card key={b.booking_id} className="glass-card border-border p-4" data-testid={`booking-${b.booking_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{b.requester_name || 'Unknown'}</p>
                    <Badge className={STATUS_COLORS[b.status] || STATUS_COLORS.pending}>{b.status}</Badge>
                    {b.service_type && <Badge variant="outline" className="text-xs capitalize">{b.service_type.replace('_', ' ')}</Badge>}
                  </div>
                  {b.message && <p className="text-sm text-muted-foreground mt-1">{b.message}</p>}
                  {b.notes && <p className="text-xs text-muted-foreground mt-1 italic">{b.notes}</p>}
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    {b.preferred_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{b.preferred_date}</span>}
                    {b.preferred_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{b.preferred_time}</span>}
                    <span>{new Date(b.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {b.status === 'pending' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      disabled={updatingId === b.booking_id}
                      onClick={() => updateStatus(b.booking_id, 'accepted')}
                      data-testid={`accept-booking-${b.booking_id}`}
                    >
                      {updatingId === b.booking_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-500 border-red-500/30"
                      disabled={updatingId === b.booking_id}
                      onClick={() => updateStatus(b.booking_id, 'declined')}
                      data-testid={`decline-booking-${b.booking_id}`}
                    >
                      <X className="w-3 h-3 mr-1" />Decline
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
